import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Call, Device } from '@twilio/voice-sdk';
import {
  CallDirection,
  CallHistoryType,
  CallProvider,
  CallStatus,
  useCreateTwilioTokenMutation,
  useGetPhoneCallParticipantQuery,
  useSubscribeToCallHistoryChangedSubscription,
  useIgnoreAxionPhoneCallMutation,
  useEndAxionPhoneCallMutation,
  IgnoreCallAction,
  CallParticipantResult,
  useCreateSystemChannelMessageMutation,
  MessageType,
  AxionClinicConfigurationStatus,
  useGetOrCreateActiveClientChannelMutation,
  GetPhoneCallParticipantDocument,
} from 'shared/types/graphql';
import PhoneCallPopover, { composeMissedCallMessage, UNKNOWN_DISPLAY_NAME } from 'shared/components/PhoneCallPopover';
import { useTwilioPhoneCallRecording } from 'shared/components/PhoneCallPopover/hooks/useTwilioPhoneCallRecording';
import { PopoverName } from 'shared/enums/PopoverName';
import { usePopover } from '../PopoverWindowProvider';
import useClinicUser from 'shared/hooks/useClinicUser';
import { formatPhoneNumber, selectPrimaryPhoneNumberBestGuess } from 'shared/utils';
import { PhoneCallParticipant, PhoneCallState } from './interfaces/PhoneCallState';
import { StagePhoneCallProps } from './interfaces/StagePhoneCallProps';
import merge from 'lodash-es/merge';
import omit from 'lodash-es/omit';
import { PhoneCallParticipantEventEntity } from './interfaces/PhoneCallParticipantEvent';
import { addDays, subDays, isPast, parseISO } from 'date-fns';
import { useCallHistory } from './hooks/useCallHistory';
import checkBrowserSupport from 'shared/components/UnsupportedBrowserPopover/checkBrowserSupport';
import { BrowserSupportFeatures } from 'shared/enums/BrowserSupportFeatures';
import { GraphQLFetchPolicies } from 'shared/enums';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { PhoneDisplayMode, setPhoneDisplayMode } from './state/phoneSlice';
import { useBrowserPermission } from 'shared/hooks/useBrowserPermission';
import { useToast } from '@televet/kibble-ui';
import { getRecentActiveServiceReminders } from 'shared/utils/getRecentActiveServiceReminders';
import { pad } from 'shared/utils/pad';
import { openChannelOverlay } from 'pages/Conversations/state/conversationsSlice';

// TODO (future): Abstract into a "DeepPartial" type
type DeepPartialPhoneCallState = Partial<
  Omit<PhoneCallState, 'participant'> & { participant: Partial<PhoneCallParticipant> }
>;

const DEFAULT_FROM_NUMBER = '+1 844 932 3838';
const DEFAULT_PHONE_CALL_STATE: PhoneCallState = {
  callId: null,
  direction: CallDirection.Outbound,
  status: null,
  result: null,
  startTime: null,
  isIgnoreMessageSent: false,
  isMuted: false,
  isRecording: false,
  recordingId: null,
  isDialPadOpen: false,
  toPhoneNumberInput: '',
  participant: {
    phoneNumber: '',
    displayName: null,
    clinicPetParentId: null,
    pets: [],
    events: [],
    channel: null,
  },
};

interface PhoneCallContext {
  isReceivingCalls: boolean;
  callProvider: CallProvider;
  showPhone: (phoneCallState?: DeepPartialPhoneCallState) => void;
  toggleIsReceivingCalls: () => void;
  stageOutgoingPhoneCall: (props: StagePhoneCallProps) => void;
  endPhoneCall: () => void;
}

const phoneCallContext = createContext<PhoneCallContext>({
  isReceivingCalls: false,
  callProvider: CallProvider.Twilio,
  showPhone: (): void => {
    throw new Error('showPhone: () => void not implemented');
  },
  toggleIsReceivingCalls: (): void => {
    throw new Error('toggleIsReceivingCalls: () => void not implemented');
  },
  stageOutgoingPhoneCall: (): void => {
    throw new Error('stageOutgoingPhoneCall: () => void not implemented');
  },
  endPhoneCall: (): void => {
    throw new Error('endPhoneCall: () => void not implemented');
  },
});

export const usePhoneCallContext = (): PhoneCallContext => useContext(phoneCallContext);

const PhoneCallProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { currentClinic, clinicUser, clinicUserId } = useClinicUser();
  const dispatch = useAppDispatch();
  const displayMode = useAppSelector((state) => state.phone.displayMode);
  const toast = useToast();
  const { popover, openPopover, closePopover } = usePopover();

  const [isReceivingCalls, setIsReceivingCalls] = useState(false);
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const { eventsAfter, eventsBefore } = useMemo(() => {
    return { eventsAfter: subDays(new Date(), 14), eventsBefore: addDays(new Date(), 14) };
  }, []);

  const [createTwilioToken] = useCreateTwilioTokenMutation({ variables: { identity: currentClinic?.id ?? '' } });
  const { createCallHistory, updateCallHistory } = useCallHistory({
    callProvider: CallProvider.Twilio,
    callType: CallHistoryType.Voice,
  });
  const [createSystemMessage] = useCreateSystemChannelMessageMutation();
  const [ignoreAxionPhoneCall] = useIgnoreAxionPhoneCallMutation();
  const [endAxionPhoneCall] = useEndAxionPhoneCallMutation();

  const [getOrCreateActiveClientChannel] = useGetOrCreateActiveClientChannelMutation();

  const callProvider = useMemo(
    () =>
      currentClinic?.axionClinicConfiguration &&
        currentClinic.axionClinicConfiguration.status === AxionClinicConfigurationStatus.Enabled
        ? CallProvider.Axion
        : CallProvider.Twilio,
    [currentClinic],
  );
  const userPhoneExtension = useMemo(() => {
    if (!currentClinic || !clinicUser) {
      return null;
    }
    return (
      clinicUser?.axionClinicUserConfigurations.find(({ clinicId }) => clinicId === currentClinic.id)?.extension ?? null
    );
  }, [clinicUser, currentClinic]);

  const clinicPhoneNumber = useMemo(() => {
    if (currentClinic?.phone && currentClinic?.isCallerIdVerified) {
      return currentClinic.phone;
    }
    return null;
  }, [currentClinic]);

  const [_phoneCallState, _setPhoneCallState] = useState<PhoneCallState>(DEFAULT_PHONE_CALL_STATE);
  const updatePhoneCallState = useCallback((updates: DeepPartialPhoneCallState): void => {
    _setPhoneCallState((state) => merge({}, state, updates));
  }, []);
  const resetPhoneCallState = (preserveKeys?: (keyof PhoneCallState)[], shouldPreserveStatusKey = false): void => {
    callRef.current = null;
    _setPhoneCallState((value) => {
      const preserveStatusKey = shouldPreserveStatusKey && value.status && [CallStatus.Queued, CallStatus.Ringing, CallStatus.InProgress].includes(value.status);
      const finalKeys = preserveKeys ? [...preserveKeys] : [];
      if (preserveStatusKey && !finalKeys.includes('status')) {
        finalKeys.push('status');
      }
      return merge({}, value, omit(DEFAULT_PHONE_CALL_STATE, finalKeys));
    });
  };

  const { data: phoneCallParticipant, refetch: refetchPhoneCallParticipant } = useGetPhoneCallParticipantQuery({
    variables: {
      clinicPetParentId: _phoneCallState.participant?.clinicPetParentId ?? '',
      eventsAfter,
      eventsBefore,
      clinicId: currentClinic?.id ?? '',
    },
    skip: !_phoneCallState.participant?.clinicPetParentId || !currentClinic?.id,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const isPhoneCallPopoverOpen = popover?.name === PopoverName.PhoneCall;

  useEffect(() => {
    if (isPhoneCallPopoverOpen) {
      resetPhoneCallState(['participant', 'isMuted', 'isDialPadOpen', 'toPhoneNumberInput', 'direction', 'callId', 'startTime'], true);
    }
  }, [isPhoneCallPopoverOpen, phoneCallParticipant?.findUniqueClinicPetParent?.id]);

  useEffect(() => {
    if (isPhoneCallPopoverOpen) {
      refetchPhoneCallParticipant();
    }
  }, [isPhoneCallPopoverOpen, refetchPhoneCallParticipant]);

  const phoneCallState = useMemo(() => {
    const clinicPetParent = phoneCallParticipant?.findUniqueClinicPetParent;
    const mergedData: DeepPartialPhoneCallState = {
      participant: merge({}, _phoneCallState.participant, {
        displayName: clinicPetParent
          ? [clinicPetParent?.firstName, clinicPetParent?.lastName].join(' ').trim()
          : _phoneCallState.participant.displayName,
        pets: clinicPetParent?.pets.map(({ id, name, speciesType, gender, dateOfBirth }) => ({
          id,
          name,
          speciesType,
          gender,
          dateOfBirth,
        })),
        events: clinicPetParent?.pets.flatMap((pet) => {
          const appointments = pet.appointments.map((appointment) => ({
            eventEntity: PhoneCallParticipantEventEntity.Appointment,
            eventEntityId: appointment.id,
            eventName: appointment.appointmentType?.name,
            timestamp: parseISO(appointment.startAt),
            petId: pet.id,
            petName: pet.name,
            petSpeciesType: pet.speciesType,
          }));
          const serviceReminders = getRecentActiveServiceReminders(pet.serviceReminders).map((serviceReminder) => ({
            eventEntity: PhoneCallParticipantEventEntity.ServiceReminder,
            eventEntityId: serviceReminder.id,
            eventName: serviceReminder.service.name,
            timestamp: parseISO(serviceReminder.dueDate),
            isPastDue:
              isPast(parseISO(serviceReminder.dueDate)) &&
              (!serviceReminder.dateLastGiven || isPast(parseISO(serviceReminder.dateLastGiven))),
            petId: pet.id,
            petName: pet.name,
            petSpeciesType: pet.speciesType,
          }));
          return [...appointments, ...serviceReminders];
        }),
        channel: clinicPetParent?.channelMembers?.[0]?.channel,
        phoneNumber:
          _phoneCallState.participant?.phoneNumber ||
          selectPrimaryPhoneNumberBestGuess(clinicPetParent?.phoneNumbers ?? []),
      }),
    };
    return merge({}, _phoneCallState, mergedData);
  }, [_phoneCallState, phoneCallParticipant]);

  /**
   * Ensure the latest phone call state is available in the `checkCallStatus` timeout callback below
   */
  const phoneCallStateRef = useRef(phoneCallState);
  useEffect(() => {
    phoneCallStateRef.current = phoneCallState;
  }, [phoneCallState]);

  /**
   * Automatically cancel an inbound ringing call when we haven't received a status update after 30 seconds
   */
  // const previousPhoneCallState = usePrevious<PhoneCallState>(phoneCallState);
  // useEffect(() => {
  //   const { status, direction, callId } = phoneCallState;
  //   if (
  //     direction === CallDirection.Inbound &&
  //     status !== previousPhoneCallState?.status &&
  //     status === CallStatus.Ringing
  //   ) {
  //     setTimeout(() => {
  //       if (phoneCallStateRef.current.status === CallStatus.Ringing && callId === phoneCallStateRef.current.callId) {
  //         updatePhoneCallState({ status: CallStatus.Canceled });
  //       }
  //     }, 30000);
  //   }
  // }, [phoneCallState, previousPhoneCallState, updatePhoneCallState]);

  const isCallOngoing = useMemo(
    () =>
      !!phoneCallState.status &&
      [CallStatus.Queued, CallStatus.Ringing, CallStatus.InProgress].includes(phoneCallState.status),
    [phoneCallState.status],
  );

  const { hasPermission, hasCheckedPermission } = useBrowserPermission({
    permissionName: 'microphone',
    skip: callProvider === CallProvider.Axion || !isPhoneCallPopoverOpen,
  });
  const isMicrophoneBlocked = hasCheckedPermission && !hasPermission;

  const setRecordingId = useCallback(
    (recordingId: string): void => {
      updatePhoneCallState({ recordingId });
    },
    [updatePhoneCallState],
  );

  const { startRecording, pauseRecording } = useTwilioPhoneCallRecording({
    recordingId: phoneCallState.recordingId,
    setRecordingId,
  });

  const toggleMinimizePopover = (): void => {
    dispatch(
      setPhoneDisplayMode({
        displayMode:
          displayMode === PhoneDisplayMode.Minimized ? PhoneDisplayMode.Maximized : PhoneDisplayMode.Minimized,
      }),
    );
  };

  const showPhone = useCallback(
    (phoneCallState?: DeepPartialPhoneCallState): void => {
      openPopover({ name: PopoverName.PhoneCall });
      updatePhoneCallState({ isDialPadOpen: true, ...phoneCallState });
    },
    [openPopover, updatePhoneCallState],
  );

  const stageOutgoingPhoneCall = (props: StagePhoneCallProps): void => {
    if ('clinicPetParentId' in props && !isCallOngoing) {
      showPhone({
        direction: CallDirection.Outbound,
        isDialPadOpen: false,
        toPhoneNumberInput: '',
        participant: { clinicPetParentId: props.clinicPetParentId, phoneNumber: '' },
      });
    }
  };

  const { isBrowserSupported } = useMemo(() => checkBrowserSupport(BrowserSupportFeatures.Voice), []);
  useEffect(() => {
    if (popover.isOpen && !isBrowserSupported) {
      openPopover({ name: PopoverName.UnsupportedBrowser });
    }
  }, [popover.isOpen, isBrowserSupported, openPopover, updatePhoneCallState]);

  useSubscribeToCallHistoryChangedSubscription({
    variables: { clinicId: currentClinic?.id ?? '', extension: userPhoneExtension },
    skip: !currentClinic?.id || callProvider !== CallProvider.Axion || !userPhoneExtension,
    onData: (options) => {
      const callHistory = options.data.data?.callHistoryChanged?.node;
      const remoteParticipant = callHistory?.participants.find((p) => !p.user);
      const currentUserParticipantResult = callHistory?.participants.find((p) => p.user?.id === clinicUserId)?.result;

      if (callHistory) {
        let startTime;
        const isNewCall = callHistory.sourceId !== phoneCallState.callId;
        const isInboundCall = callHistory.direction === CallDirection.Inbound;
        const isInProgressStatus = callHistory.status === CallStatus.InProgress;

        // When a call is already in progress, suppress updates for any other calls to/from this extension
        if (isNewCall && isCallOngoing && isPhoneCallPopoverOpen) {
          // TODO (future): Handle simultaneous incoming calls
          return;
        }

        // For outbound calls, set the start time immediately
        if (!isInboundCall && isNewCall) {
          startTime = new Date();
        }

        // For inbound calls, set the start time when the call is accepted
        if (isInboundCall) {
          if (isNewCall) {
            startTime = isInProgressStatus ? new Date() : null;
          } else if (isInProgressStatus && !phoneCallState.startTime) {
            startTime = new Date();
          }
        }

        if (
          currentUserParticipantResult &&
          [
            CallParticipantResult.Ignored,
            CallParticipantResult.IgnoredWithMessage,
            CallParticipantResult.Rejected,
          ].includes(currentUserParticipantResult)
        ) {
          closePopover();
        }

        updatePhoneCallState({
          callId: callHistory.sourceId,
          direction: callHistory.direction,
          status: callHistory.status,
          result: currentUserParticipantResult,
          startTime,
          participant: {
            phoneNumber: remoteParticipant?.number ?? '',
            displayName: remoteParticipant?.clinicPetParent ? remoteParticipant?.number : UNKNOWN_DISPLAY_NAME,
            clinicPetParentId: remoteParticipant?.clinicPetParent?.id ?? null,
          },
        });
      }
      if (!popover.isOpen && !currentUserParticipantResult) {
        showPhone({ isDialPadOpen: false });
      }
    },
  });

  const getInitializedDevice = useCallback(async (): Promise<Device | undefined> => {
    if (deviceRef.current) {
      return deviceRef.current;
    }

    const { data } = await createTwilioToken();
    const token = data?.createTwilioToken?.token;

    if (!token) {
      console.error('Error initializing device');
      toast({
        status: 'error',
        description: 'An error occurred while starting the phone call. Please refresh the page and try again.',
        duration: 10000,
      });
      return;
    }

    const device = new Device(token, {
      logLevel: 'info',
      appName: 'clinic-web',
      closeProtection: true,
      enableImprovedSignalingErrorPrecision: true,
      tokenRefreshMs: 30000, // 30 seconds
    });

    deviceRef.current = device;

    device.on(Device.EventName.Registered, (device) => {
      console.log('Device is registered, device: ', device);
    });

    device.on(Device.EventName.Unregistered, (device) => {
      console.log('Device is unregistered, device: ', device);
    });

    device.on(Device.EventName.Error, (error, call) => {
      console.error('Twilio Device received error: ', error, call);
    });

    device.on(Device.EventName.TokenWillExpire, async (device) => {
      // TODO (future): Investigate why this event callback isn't firing, even after
      // reducing the token TTL on the BE to 90 seconds.
      console.log('Device token will expire, device: ', device);
      const { data } = await createTwilioToken();
      const token = data?.createTwilioToken?.token;
      if (token) {
        device.updateToken(token);
      }
    });

    device.on(Device.EventName.Incoming, async (call: Call) => {
      updatePhoneCallState({
        direction: CallDirection.Inbound,
        status: CallStatus.Ringing,
        participant: {
          phoneNumber: call.parameters.From,
          displayName: call.parameters.From,
          clinicPetParentId: call.customParameters.get('clinicPetParentId'),
        },
      });
      callRef.current = call;

      if (!popover.isOpen) {
        showPhone({ isDialPadOpen: false });
      }
    });

    return device;
  }, [createTwilioToken, popover.isOpen, toast, showPhone, updatePhoneCallState]);

  const sendCallCompletedSystemMessage = useCallback(
    (duration: number) => {
      const { participant } = phoneCallState;
      if (!participant.clinicPetParentId) {
        return;
      }
      const hours = Math.floor(duration / 60 / 60);
      const minutes = Math.floor(duration / 60) - hours * 60;
      const seconds = Math.round(duration % 60);

      createSystemMessage({
        variables: {
          data: {
            body: `Phone call made for ${`${pad(hours)}:${pad(minutes)}:${pad(seconds)}`} to ${participant.displayName
              } at ${formatPhoneNumber(participant.phoneNumber)}`,
            petParentIds: [participant.clinicPetParentId],
            messageType: MessageType.Note,
          },
        },
      });
    },
    [phoneCallState, createSystemMessage],
  );

  // Accept an incoming phone call
  const acceptPhoneCall = (): void => {
    if (callProvider === CallProvider.Twilio) {
      callRef.current?.accept();
    }
  };

  // Ignore an incoming phone call
  const ignorePhoneCall = async (callAction: IgnoreCallAction): Promise<void> => {
    if (callProvider === CallProvider.Twilio) {
      // TODO (future): Put this in a separate "reject" callback for incoming Twilio calls
      callRef.current?.reject();
      updatePhoneCallState({ status: CallStatus.Canceled });
    } else if (callProvider === CallProvider.Axion) {
      if (!phoneCallState.callId) {
        console.error('ignorePhoneCall: No callId found');
        return;
      }
      if (clinicUserId) {
        ignoreAxionPhoneCall({
          variables: { callId: phoneCallState.callId, userId: clinicUserId, action: callAction },
        });
        if (callAction === IgnoreCallAction.IgnoreAndSendChannelMessage) {
          updatePhoneCallState({
            isIgnoreMessageSent: true,
          });
          // handle send message part
          const clinicPetParentId = phoneCallState.participant.clinicPetParentId;
          if (clinicPetParentId) {
            const { data } = await getOrCreateActiveClientChannel({
              variables: { data: { clinicPetParentIds: [clinicPetParentId] } },
              refetchQueries: [GetPhoneCallParticipantDocument],
            });
            const channelId = data?.getOrCreateActiveClientChannel?.id;
            if (phoneCallState.status === CallStatus.Ringing) {
              dispatch(
                openChannelOverlay({
                  channelId,
                  draftMessageText: composeMissedCallMessage({ clinicName: currentClinic?.name }),
                }),
              );
            }
          }
        }
      }
    }
  };

  // End an ongoing phone call
  const endPhoneCall = (): void => {
    if (callProvider === CallProvider.Twilio) {
      deviceRef.current?.disconnectAll();
    } else if (callProvider === CallProvider.Axion && phoneCallState.callId && clinicUserId) {
      endAxionPhoneCall({
        variables: { callId: phoneCallState.callId, userId: clinicUserId },
      });
    }
  };

  const startPhoneCall = async (props: StagePhoneCallProps): Promise<void> => {
    const toPhoneNumber =
      'phoneNumber' in props && !!props.phoneNumber ? props.phoneNumber : phoneCallState.participant.phoneNumber;

    updatePhoneCallState({
      direction: CallDirection.Outbound,
      status: CallStatus.Queued,
    });

    if ('clinicPetParentId' in props) {
      updatePhoneCallState({
        participant: {
          clinicPetParentId: props.clinicPetParentId,
        },
      });
    }

    if ('phoneNumber' in props) {
      updatePhoneCallState({ participant: { phoneNumber: props.phoneNumber, displayName: UNKNOWN_DISPLAY_NAME } });
    }

    const device = await getInitializedDevice();

    if (!device) {
      return;
    }

    const fromPhoneNumber = clinicPhoneNumber ?? DEFAULT_FROM_NUMBER;

    const call = await device.connect({
      params: {
        from: fromPhoneNumber,
        to: toPhoneNumber,
        shouldRecord: phoneCallState.isRecording.toString(),
      },
    });
    call.mute(phoneCallState.isMuted);

    openPopover({ name: PopoverName.PhoneCall });

    callRef.current = call;
    let callSid = call.parameters.CallSid;
    let callHistoryId: undefined | string;
    let startTime: undefined | Date;

    const createOrUpdateCallHistory = async ({
      status,
      duration,
    }: {
      status?: CallStatus;
      duration?: number;
    }): Promise<void> => {
      if (callSid && !callHistoryId) {
        const callHistory = await createCallHistory({
          sourceId: callSid,
          from: fromPhoneNumber,
          clinicPetParentPhoneNumber: toPhoneNumber,
          clinicPetParentId: phoneCallState.participant?.clinicPetParentId ?? undefined,
          direction: CallDirection.Outbound,
          status,
        });
        callHistoryId = callHistory?.id;
      } else if (callHistoryId) {
        updateCallHistory({
          id: callHistoryId,
          status,
          duration,
        });
      }
    };

    call.on('ringing', () => {
      updatePhoneCallState({ status: CallStatus.Ringing, isDialPadOpen: false, toPhoneNumberInput: '' });
      createOrUpdateCallHistory({ status: CallStatus.Ringing });
    });
    call.on('accept', async (call) => {
      startTime = new Date();
      updatePhoneCallState({ status: CallStatus.InProgress, startTime });
      callSid = call.parameters.CallSid;
      await createOrUpdateCallHistory({ status: CallStatus.InProgress });
      if (phoneCallState.isRecording) {
        startRecording(callSid);
      }
    });
    call.on('cancel', () => {
      updatePhoneCallState({ status: CallStatus.Canceled });
      createOrUpdateCallHistory({ status: CallStatus.Canceled });
    });
    call.on('reject', () => {
      updatePhoneCallState({ status: CallStatus.Canceled });
      createOrUpdateCallHistory({ status: CallStatus.NoAnswer });
    });
    call.on('disconnect', (call) => {
      callSid = call.parameters.CallSid;
      const now = Date.now();
      const duration = Math.round((now - (startTime?.getTime() ?? now)) / 1000);
      createOrUpdateCallHistory({
        status: CallStatus.Completed,
        duration,
      });
      updatePhoneCallState({ status: CallStatus.Completed });
      sendCallCompletedSystemMessage(duration);
    });
    call.on('error', (error) => {
      console.error('Phone call error', error);
      toast({
        status: 'error',
        description: 'An error occurred during the phone call. Please refresh the page and try again.',
        duration: 10000,
      });
    });
    call.on('mute', (isMuted) => {
      updatePhoneCallState({ isMuted });
    });
  };

  const toggleMute = (): void => {
    const newIsMuted = !phoneCallState.isMuted;
    updatePhoneCallState({ isMuted: newIsMuted });
    callRef.current?.mute(newIsMuted);
  };

  const toggleRecording = (): void => {
    const newIsRecording = !phoneCallState.isRecording;
    if (!newIsRecording && callRef.current) {
      pauseRecording();
    }
    updatePhoneCallState({ isRecording: newIsRecording });
  };

  const toggleDialPad = (): void => {
    updatePhoneCallState({ isDialPadOpen: !phoneCallState.isDialPadOpen });
  };

  const sendDigits = (value: string): void => {
    callRef.current?.sendDigits(value);
  };

  const clearCallParticipant = (): void => {
    if (!isCallOngoing) {
      resetPhoneCallState(['isMuted', 'isDialPadOpen', 'toPhoneNumberInput']);
      updatePhoneCallState({ isDialPadOpen: true });
    }
  };

  const handlePhoneNumberInputChange = (value: string): void => {
    updatePhoneCallState({ toPhoneNumberInput: value });
    if (!isCallOngoing && (phoneCallState.participant.clinicPetParentId || phoneCallState.participant.phoneNumber)) {
      clearCallParticipant();
    }
  };

  const toggleIsReceivingCalls = (): void => {
    setIsReceivingCalls((v) => !v);
  };

  // TODO (future): Reintroduce this if/when we support Twilio incoming calls
  // useEffect(() => {
  //   async function handlePhoneStateChange(): Promise<void> {
  //     const device = await getInitializedDevice();
  //     if (!device) {
  //       return;
  //     }
  //     if (
  //       isReceivingCalls &&
  //       device?.state &&
  //       ![Device.State.Registered, Device.State.Registering].includes(device.state)
  //     ) {
  //       device?.register();
  //       console.log('*** registering device');
  //     } else if (!isReceivingCalls && device?.state && device.state === Device.State.Registered) {
  //       console.log('*** un-registering device');
  //       device?.unregister();
  //     }
  //   }
  //   handlePhoneStateChange();
  // }, [isReceivingCalls, getInitializedDevice]);

  return (
    <phoneCallContext.Provider
      value={{
        isReceivingCalls: isReceivingCalls,
        callProvider,
        showPhone,
        stageOutgoingPhoneCall,
        endPhoneCall,
        toggleIsReceivingCalls,
      }}
    >
      {isPhoneCallPopoverOpen && (
        <PhoneCallPopover
          phoneCallState={phoneCallState}
          isCallOngoing={isCallOngoing}
          callProvider={callProvider}
          displayMode={displayMode}
          isMicrophoneBlocked={isMicrophoneBlocked}
          onToggleMute={toggleMute}
          onToggleRecording={toggleRecording}
          onToggleDialPad={toggleDialPad}
          onPhoneNumberInputChange={handlePhoneNumberInputChange}
          onClearCallParticipant={clearCallParticipant}
          onSendDigits={sendDigits}
          onStartPhoneCall={startPhoneCall}
          onAcceptPhoneCall={acceptPhoneCall}
          onIgnorePhoneCall={ignorePhoneCall}
          onEndPhoneCall={endPhoneCall}
          onToggleMinimized={toggleMinimizePopover}
          onClosePopover={closePopover}
        />
      )}
      {children}
    </phoneCallContext.Provider>
  );
};

export default PhoneCallProvider;
